'use client';

import { useMutationObserver } from '@react-hooks-library/core';
import { useField, useFormikContext } from 'formik';
import { type ComponentPropsWithRef, useEffect, useRef } from 'react';

/**
 * Props definition for the Tracking Field component.
 */

export type TrackingFieldProps = ComponentPropsWithRef<'input'> & {
  name: string;
};

/**
 * Tracking Field Component.
 */

export const HiddenField = (props: TrackingFieldProps) => {
  const { name, value, ref, ...rest } = props;
  return <input type="hidden" name={name} value={value} ref={ref} {...rest} />;
};

/**
 * Formik-Controlled Tracking Field.
 */

export const FormikTrackingField = ({ name, ...props }: TrackingFieldProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const { setFieldValue } = useFormikContext();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta, helpers] = useField(name);

  // Automatically extract the value from the URL on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const urlValue = searchParams.get(name);

      if (urlValue && field.value !== urlValue) {
        setFieldValue(name, urlValue);
      }
    }
  }, [name, field.value, setFieldValue]);

  // Need the useObserver to detect if the hidden field has been changed outside of React (i.e.: Google Tag Manager)
  useMutationObserver(
    ref,
    (mutations) => {
      for (const mutation of mutations) {
        const target = mutation.target as HTMLInputElement;
        if (target.type && target.type === 'hidden') helpers.setValue(target.value);
      }
    },
    { attributes: true, attributeFilter: ['value'] }
  );

  return <HiddenField ref={ref} {...field} {...props} />;
};

export default HiddenField;
